import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'egs:auth:permissions';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly _codes = signal<ReadonlySet<string>>(
    // Restore from sessionStorage so the nav is populated immediately after a
    // full-page reload (e.g. language switch) without waiting for /me/.
    PermissionService._loadFromStorage(),
  );

  private static _loadFromStorage(): ReadonlySet<string> {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore parse errors
    }
    return new Set();
  }

  /** Replace the full permission set (called after every /me response). */
  setPermissions(codes: string[]): void {
    this._codes.set(new Set(codes));
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
    } catch {
      // ignore storage errors (private browsing quota, etc.)
    }
  }

  /** Clear all permissions (called on logout). */
  clear(): void {
    this._codes.set(new Set());
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  /** True when the user holds the given permission code. */
  has(code: string): boolean {
    return this._codes().has(code);
  }

  /** True when the user holds at least one of the given codes. */
  hasAny(codes: string[]): boolean {
    const set = this._codes();
    return codes.some((c) => set.has(c));
  }

  /** Read-only signal exposing the raw set for template reactivity. */
  readonly snapshot = this._codes.asReadonly();
}
